import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, Code, Cloud, Users, Zap, ShoppingCart, Heart, BookOpen, CreditCard } from 'lucide-react';

interface ServiceCardProps {
  title: string;
  description: string;
  icon: string;
  keyOfferings?: string[];
  className?: string;
  onClick?: () => void;
}

const iconComponents: Record<string, React.ComponentType<{ className?: string }>> = {
  Brain,
  Code,
  Cloud,
  Users,
  Zap,
  ShoppingCart,
  Heart,
  BookOpen,
  CreditCard
};

const ServiceCard: React.FC<ServiceCardProps> = ({
  title,
  description,
  icon,
  keyOfferings,
  className = '',
  onClick
}) => {
  const IconComponent = iconComponents[icon] || Users;

  return (
    <motion.div
      className={`card p-6 group cursor-pointer ${className}`}
      onClick={onClick}
      whileHover={{ y: -5, scale: 1.02 }}
      transition={{ duration: 0.2 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Icon */}
      <div className="mb-4">
        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center group-hover:bg-primary-600 transition-colors duration-300">
          <IconComponent className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors duration-300" />
        </div>
      </div>

      {/* Content */}
      <div className="mb-4">
        <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
          {title}
        </h3>
        <p className="text-gray-600 leading-relaxed">
          {description}
        </p>
      </div>

      {/* Key Offerings */}
      {keyOfferings && keyOfferings.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Key Offerings:</h4>
          <ul className="space-y-1">
            {keyOfferings.slice(0, 3).map((offering, index) => (
              <li key={index} className="text-sm text-gray-600 flex items-center">
                <span className="w-1.5 h-1.5 bg-primary-400 rounded-full mr-2 flex-shrink-0"></span>
                {offering}
              </li>
            ))}
          </ul>
          {keyOfferings.length > 3 && (
            <p className="text-sm text-primary-600 mt-2">
              +{keyOfferings.length - 3} more services
            </p>
          )}
        </div>
      )}

      {/* Learn More Link */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors duration-200">
          Learn More
        </span>
        <ArrowRight className="w-4 h-4 text-primary-600 group-hover:text-primary-700 group-hover:translate-x-1 transition-all duration-200" />
      </div>
    </motion.div>
  );
};

export default ServiceCard;